import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { entriesForModule, loadWhatsNewEntries, localDay, unseenEntries } from '../../data/whatsNew/registry';
import { fetchWhatsNewState, markWhatsNewSeen, markWhatsNewShown } from './whatsNewApi';
import './WhatsNewLauncher.css';

// The dialog (and its CSS) is its own chunk: most module opens never show it.
const WhatsNewModal = lazy(() => import(/* webpackChunkName: "whats-new" */ './WhatsNewModal'));

function dayOf(today) {
  return typeof today === 'function' ? today() : today;
}

/**
 * What's new state for one module.
 *
 * When `active` turns true (the module is on screen) it loads the entry list
 * and the person's seen state. If there are unseen entries for the module and
 * the popup has not been dismissed today in this module, it opens with those
 * entries (newest first). "Got it" marks every entry shown as seen; closing
 * any other way (Esc, ×, backdrop) records today, so it comes back tomorrow.
 * The day is recorded on close, not on open: the module can remount while
 * the app settles its route/permissions, and a popup recorded-but-unmounted
 * would be lost for the whole day.
 * `openHistory()` shows every entry for the module, any time.
 */
export function useWhatsNew(module, { active = true, autoOpen = true, today = localDay } = {}) {
  const [entries, setEntries] = useState(null);
  const [state, setState] = useState(null);
  const [dialog, setDialog] = useState(null); // { mode: 'new' | 'history', entries }

  useEffect(() => {
    if (!active) {
      // Module left the screen (e.g. Products under another module): never leave its dialog floating.
      setDialog(null);
      return undefined;
    }
    let cancelled = false;
    Promise.all([loadWhatsNewEntries().catch(() => []), fetchWhatsNewState()])
      .then(([loaded, loadedState]) => {
        if (cancelled) return;
        setEntries(loaded);
        setState(loadedState);
        if (!autoOpen) return;
        const unseen = unseenEntries(loaded, module, loadedState.seen);
        const day = dayOf(today);
        if (!unseen.length || loadedState.lastShownDay?.[module] === day) return;
        setDialog((current) => current || { mode: 'new', entries: unseen });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [active, autoOpen, module, today]);

  const unseenCount = entries && state ? unseenEntries(entries, module, state.seen).length : 0;

  const openHistory = useCallback(async () => {
    const list = entries || await loadWhatsNewEntries().catch(() => []);
    if (!entries) setEntries(list);
    setDialog({ mode: 'history', entries: entriesForModule(list, module) });
  }, [entries, module]);

  const close = useCallback(() => {
    const wasAuto = dialog?.mode === 'new';
    setDialog(null);
    if (!wasAuto) return;
    const day = dayOf(today);
    setState((cur) => ({ ...(cur || {}), lastShownDay: { ...(cur?.lastShownDay || {}), [module]: day } }));
    markWhatsNewShown(module, day).catch(() => {});
  }, [dialog, module, today]);

  const acknowledge = useCallback(async (shown) => {
    const ids = (shown || dialog?.entries || []).map((entry) => entry.id);
    setDialog(null);
    // Optimistic: the unseen dot clears at once; the server copy follows.
    const at = new Date().toISOString();
    setState((cur) => ({
      ...(cur || {}),
      seen: { ...(cur?.seen || {}), ...Object.fromEntries(ids.map((id) => [id, at])) }
    }));
    const next = await markWhatsNewSeen(ids, { module, day: dayOf(today) });
    setState((cur) => ({ ...(cur || {}), seen: { ...(cur?.seen || {}), ...(next?.seen || {}) } }));
  }, [dialog, module, today]);

  return { dialog, unseenCount, openHistory, close, acknowledge };
}

/** The header button; `whatsNew` is the useWhatsNew() result. */
export function WhatsNewButton({ whatsNew, className = '', label = "What's new", compact = false }) {
  const unseenCount = whatsNew?.unseenCount || 0;
  const classes = ['whats-new-launcher', compact ? 'whats-new-launcher--compact' : '', unseenCount ? 'whats-new-launcher--unseen' : '', className].filter(Boolean).join(' ');
  return (
    <button
      type="button"
      className={classes}
      onClick={whatsNew?.openHistory}
      title={unseenCount ? `${unseenCount} new update${unseenCount === 1 ? '' : 's'}` : "What's new in this module"}
      aria-label={unseenCount ? `${label} (${unseenCount} new)` : label}
    >
      <span aria-hidden="true" className="whats-new-launcher__icon">✨</span>
      <span className={compact ? 'whats-new-launcher__sr' : 'whats-new-launcher__label'}>{label}</span>
      {unseenCount ? <span className="whats-new-launcher__dot" aria-hidden="true" /> : null}
    </button>
  );
}

/** Renders the dialog while open (lazy chunk). Mount once per module screen. */
export function WhatsNewDialogHost({ whatsNew, module, darkMode = false }) {
  if (!whatsNew?.dialog) return null;
  return (
    <Suspense fallback={null}>
      <WhatsNewModal
        module={module}
        mode={whatsNew.dialog.mode}
        entries={whatsNew.dialog.entries}
        darkMode={darkMode}
        onClose={whatsNew.close}
        onAcknowledge={whatsNew.acknowledge}
      />
    </Suspense>
  );
}

/**
 * "What's new" button + popup for a module header, in one piece. Screens whose
 * header remounts (Ship) call useWhatsNew() once and use WhatsNewButton +
 * WhatsNewDialogHost instead.
 *
 * @param {'ship'|'products'|'picking'} module
 * @param {boolean} active      module is on screen (the auto popup checks then)
 * @param {string}  className   extra class for the button (match the header's buttons)
 */
export default function WhatsNewLauncher({ module, active = true, autoOpen = true, darkMode = false, className = '', label }) {
  const whatsNew = useWhatsNew(module, { active, autoOpen });
  return (
    <>
      <WhatsNewButton whatsNew={whatsNew} className={className} label={label} />
      <WhatsNewDialogHost whatsNew={whatsNew} module={module} darkMode={darkMode} />
    </>
  );
}
